import { createContext, useCallback, useContext, useMemo, useState } from "react";
import { DEFAULT_LANG, translations } from "./translations";

const LanguageContext = createContext(null);

const STORAGE_KEY = "itm.lang";

function readInitialLang() {
  if (typeof window === "undefined") return DEFAULT_LANG;
  try {
    const stored = window.localStorage.getItem(STORAGE_KEY);
    if (stored === "en" || stored === "ru") return stored;
  } catch (_e) {
    // ignore storage failures (SSR, privacy mode, etc.)
  }
  return DEFAULT_LANG;
}

export function LanguageProvider({ children }) {
  const [lang, setLang] = useState(readInitialLang);

  const changeLang = useCallback((next) => {
    if (next !== "en" && next !== "ru") return;
    setLang(next);
    try {
      window.localStorage.setItem(STORAGE_KEY, next);
      document.documentElement.setAttribute("lang", next);
    } catch (_e) {
      // ignore
    }
  }, []);

  const toggle = useCallback(() => {
    setLang((prev) => {
      const next = prev === "en" ? "ru" : "en";
      try {
        window.localStorage.setItem(STORAGE_KEY, next);
        document.documentElement.setAttribute("lang", next);
      } catch (_e) {
        // ignore
      }
      return next;
    });
  }, []);

  const value = useMemo(
    () => ({
      lang,
      t: translations[lang],
      setLang: changeLang,
      toggle,
    }),
    [lang, changeLang, toggle],
  );

  return <LanguageContext.Provider value={value}>{children}</LanguageContext.Provider>;
}

export function useLang() {
  const ctx = useContext(LanguageContext);
  if (!ctx) throw new Error("useLang must be used inside <LanguageProvider>");
  return ctx;
}
